/**
 * Shared color utility for Squeezo value visualization.
 *
 * Maps a 0-100 value through a gradient:
 * 0 (bullish/blue) → 50 (neutral/bone→gold) → 100 (bearish/red)
 *
 * Brand spec: NO GREEN ANYWHERE. Note that interpolating directly from
 * the Up/Call blue to Accent Gold passes through olive in RGB space, so a
 * bone (#F4F2ED) stop sits at the crossover to keep the ramp clean.
 */

const COLOR_STOPS = [
  { pos: 0, r: 28, g: 118, b: 162 },   // #1c76a2 Up/Call blue
  { pos: 25, r: 120, g: 170, b: 195 }, // lighter blue
  { pos: 42, r: 244, g: 242, b: 237 }, // #f4f2ed bone — neutral crossover
  { pos: 58, r: 194, g: 161, b: 78 },  // #c2a14e Accent Gold
  { pos: 78, r: 211, g: 120, b: 66 },  // gold → red bridge
  { pos: 100, r: 216, g: 72, b: 59 },  // #d8483b Down/Put red
];

/** Returns RGB components for a squeezo value (0-100). */
export function getSqueezoColorRgb(value: number): { r: number; g: number; b: number } {
  const clamped = Math.max(0, Math.min(100, value));

  let lower = COLOR_STOPS[0];
  let upper = COLOR_STOPS[COLOR_STOPS.length - 1];
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    if (clamped >= COLOR_STOPS[i].pos && clamped <= COLOR_STOPS[i + 1].pos) {
      lower = COLOR_STOPS[i];
      upper = COLOR_STOPS[i + 1];
      break;
    }
  }

  const range = upper.pos - lower.pos;
  const t = range === 0 ? 0 : (clamped - lower.pos) / range;
  return {
    r: Math.round(lower.r + (upper.r - lower.r) * t),
    g: Math.round(lower.g + (upper.g - lower.g) * t),
    b: Math.round(lower.b + (upper.b - lower.b) * t),
  };
}

/** Returns an `rgb(r, g, b)` CSS string for a squeezo value (0-100). */
export function getSqueezoColor(value: number): string {
  const { r, g, b } = getSqueezoColorRgb(value);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Reversed gradient for the toolbar gauge.
 * Low values = red (bearish), high values = blue (bullish).
 * More color change concentrated in the 35-65 range where values typically sit.
 * Same no-green constraint and bone crossover as COLOR_STOPS above.
 */
const GAUGE_COLOR_STOPS = [
  { pos: 0, r: 216, g: 72, b: 59 },    // #d8483b Down/Put red
  { pos: 30, r: 219, g: 92, b: 62 },   // warm red (flat-ish region)
  { pos: 42, r: 211, g: 120, b: 66 },  // red → gold bridge
  { pos: 52, r: 194, g: 161, b: 78 },  // #c2a14e Accent Gold (midpoint)
  { pos: 62, r: 244, g: 242, b: 237 }, // #f4f2ed bone — neutral crossover
  { pos: 75, r: 120, g: 170, b: 195 }, // lighter blue (settling)
  { pos: 100, r: 28, g: 118, b: 162 }, // #1c76a2 Up/Call blue
];

/** Returns RGB components using the gauge (reversed) gradient. */
export function getSqueezoGaugeColorRgb(value: number): { r: number; g: number; b: number } {
  const clamped = Math.max(0, Math.min(100, value));

  let lower = GAUGE_COLOR_STOPS[0];
  let upper = GAUGE_COLOR_STOPS[GAUGE_COLOR_STOPS.length - 1];
  for (let i = 0; i < GAUGE_COLOR_STOPS.length - 1; i++) {
    if (clamped >= GAUGE_COLOR_STOPS[i].pos && clamped <= GAUGE_COLOR_STOPS[i + 1].pos) {
      lower = GAUGE_COLOR_STOPS[i];
      upper = GAUGE_COLOR_STOPS[i + 1];
      break;
    }
  }

  const range = upper.pos - lower.pos;
  const t = range === 0 ? 0 : (clamped - lower.pos) / range;
  return {
    r: Math.round(lower.r + (upper.r - lower.r) * t),
    g: Math.round(lower.g + (upper.g - lower.g) * t),
    b: Math.round(lower.b + (upper.b - lower.b) * t),
  };
}

/** Returns an `rgb(r, g, b)` CSS string using the gauge (reversed) gradient. */
export function getSqueezoGaugeColor(value: number): string {
  const { r, g, b } = getSqueezoGaugeColorRgb(value);
  return `rgb(${r}, ${g}, ${b})`;
}

/* ── Flat zone colours, for the header gauge ───────────────────────────── */

export type SqueezoZone = 'bearish' | 'neutral' | 'bullish';

/**
 * ⚠️ These thresholds are the ones the gauge's own tooltip legend already
 * publishes, and the ones `zoneLabel` uses. The reversed GAUGE gradient above
 * contradicted both: it rendered value 20 RED while the legend called 0-45
 * bullish, and value 80 BLUE while the legend called 65-100 bearish. Anything
 * new should key off this, not off that ramp.
 */
export function getSqueezoZone(value: number): SqueezoZone {
  const v = Math.round(value);
  if (v >= 65) return 'bearish';
  if (v <= 45) return 'bullish';
  return 'neutral';
}

const ZONE_COLOR: Record<SqueezoZone, string> = {
  bearish: '#D8483B', // bear-red
  bullish: '#1C76A2', // bull-blue — BLUE in this palette
  neutral: '#C2A14E', // accent-yellow
};

/**
 * One flat colour per zone, for the gauge that sits in page headers.
 *
 * A continuous ramp is right inside the full Squeezo widget, where there is
 * room to read a gradient. At 16px in a toolbar it only produced muddy
 * in-between colours — olive, salmon — that matched nothing else on screen and
 * told the reader nothing the number was not already saying.
 */
export function getSqueezoZoneColor(value: number): string {
  return ZONE_COLOR[getSqueezoZone(value)];
}
